/**
 * Display-only removal of the curriculum prefix that level and class names carry
 * ("FBISE Primary", "FBISE Class 1-A", "BISE Punjab Class 2").
 *
 * The prefix stays in the stored names on purpose: the same class name under two boards must
 * not collide on the server, where students and subjects resolve a class by bare name. So
 * `manualData`, lesson-planning keys and every payload keep the full name; only what the user
 * READS goes through `stripCurriculumPrefix`. Never write a stripped name back into state or
 * send it to the API — filters, edits and saves keep the stored name.
 *
 * What is shown follows one rule (bugs 375/377): the plain name, with the curriculum's short code
 * in front only when another curriculum in the school has a level or class reading the same
 * (`buildCurriculumNameLabeler`, `formatCurriculumName`).
 *
 * Used by the onboarding wizard and by the institute pages (through `buildSchoolCurricula`).
 * node-api mirrors the same rules in components/onboarding/curriculumDisplayNames.js.
 */

const trim = (value: unknown): string => String(value ?? '').trim()
const lower = (value: unknown): string => trim(value).toLowerCase()

/** The bracketed short form of a curriculum name ("Federal Board (FBISE)" → "FBISE"), or "". */
export const bracketedCurriculumCode = (curriculum: unknown): string =>
  trim(/\(([^()]+)\)\s*$/.exec(trim(curriculum))?.[1])

/** Lower-cased words of a name, brackets and punctuation dropped ("Federal Board (FBISE)"). */
const wordsOf = (value: unknown): string[] =>
  lower(value)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)

/** True when `run` is a whole-word stretch of `curriculumWords` ("BISE Punjab" in its name). */
const isPartOfName = (run: readonly string[], curriculumWords: readonly string[]) =>
  run.length > 0 &&
  curriculumWords.some((_, start) => run.every((word, i) => curriculumWords[start + i] === word))

/**
 * The longest run of leading words that most of the names start with (every name when there are
 * only one or two), leaving each name at least one word of its own, and made of whole words of
 * the curriculum's own name ("FBISE" from "FBISE Pre-primary 1" / "FBISE Pre-primary 2"; "BISE
 * Punjab" from "BISE Punjab Class 1" / "BISE Punjab Class 2"). Most, not all: Cambridge stores
 * "Cambridge Grade 1" … "Cambridge Grade 11" next to "AS Level" / "A2 Level", and its level names
 * ("Upper Secondary (IGCSE / O Level)") carry no prefix at all. "Grade" / "Class" / "Year" are
 * shared too but name the ladder, not the board, so they never count. "" when there is none.
 */
export const sharedCurriculumPrefix = (curriculum: unknown, classNames: readonly unknown[]) => {
  const names = classNames
    .map(trim)
    .filter(Boolean)
    .map((name) => name.split(/\s+/))
  if (names.length === 0) return ''
  const curriculumWords = wordsOf(curriculum)
  const runs = new Map<string, {run: string; count: number}>()
  names.forEach((words) => {
    for (let n = 1; n < words.length; n += 1) {
      const run = words.slice(0, n)
      if (!isPartOfName(run.flatMap(wordsOf), curriculumWords)) break
      const key = lower(run.join(' '))
      const entry = runs.get(key) ?? {run: run.join(' '), count: 0}
      entry.count += 1
      runs.set(key, entry)
    }
  })
  const needed = names.length <= 2 ? names.length : Math.floor(names.length / 2) + 1
  return Array.from(runs.values())
    .filter((entry) => entry.count >= needed)
    .reduce((best, entry) => (entry.run.length > best.length ? entry.run : best), '')
}

/**
 * Prefixes a name under `curriculum` may start with, longest first: the curriculum's short code
 * (what the wizard writes — "FBISE", "BISE KP"), the one derived from the curriculum's own class
 * names (the same rule the level drawer uses when it adds a class), the bracketed short form of
 * the curriculum ("Federal Board (FBISE)" → "FBISE") and the curriculum name itself.
 */
export const curriculumPrefixes = (
  curriculum: unknown,
  classNames: readonly unknown[] = [],
  shortCode?: unknown
): string[] => {
  const name = trim(curriculum)
  if (!name) return []
  const candidates = [
    trim(shortCode),
    sharedCurriculumPrefix(name, classNames),
    bracketedCurriculumCode(name),
    name,
  ].filter(Boolean)
  const unique = new Map<string, string>()
  candidates.forEach((c) => {
    if (!unique.has(lower(c))) unique.set(lower(c), c)
  })
  return Array.from(unique.values()).sort((a, b) => b.length - a.length)
}

/**
 * `name` without a leading prefix from `prefixes` (case-insensitive, followed by a space).
 * A name that is only the prefix, or does not start with one, comes back unchanged.
 */
export const stripCurriculumPrefix = (name: unknown, prefixes: readonly string[]): string => {
  const full = trim(name)
  const fullLower = full.toLowerCase()
  for (const prefix of prefixes) {
    const p = lower(prefix)
    if (p && fullLower.startsWith(`${p} `)) {
      const rest = full.slice(p.length).trimStart()
      if (rest) return rest
    }
  }
  return full
}

export type CurriculumPrefixIndex = {
  /** Prefixes per lower-cased curriculum name. */
  byCurriculum: Map<string, string[]>
  /** Every known prefix, longest first — for a name whose curriculum is not at hand. */
  all: string[]
}

/**
 * Build the prefix lookup once from rows that carry `name` (and `grade`) plus `curriculum` and,
 * when known, the curriculum's `shortCode`.
 */
export const buildCurriculumPrefixIndex = (
  rows: ReadonlyArray<{name?: unknown; grade?: unknown; curriculum?: unknown; shortCode?: unknown}>,
  extraCurricula: readonly unknown[] = []
): CurriculumPrefixIndex => {
  const namesByCurriculum = new Map<
    string,
    {curriculum: string; shortCode: string; names: string[]}
  >()
  const add = (curriculum: unknown, shortCode: unknown, ...names: unknown[]) => {
    const c = trim(curriculum)
    if (!c) return
    const entry = namesByCurriculum.get(c.toLowerCase()) ?? {
      curriculum: c,
      shortCode: '',
      names: [],
    }
    if (!entry.shortCode) entry.shortCode = trim(shortCode)
    names.forEach((n) => {
      const t = trim(n)
      if (t) entry.names.push(t)
    })
    namesByCurriculum.set(c.toLowerCase(), entry)
  }
  rows.forEach((row) => add(row?.curriculum, row?.shortCode, row?.name))
  extraCurricula.forEach((c) => add(c, ''))
  const byCurriculum = new Map<string, string[]>()
  namesByCurriculum.forEach(({curriculum, shortCode, names}, key) => {
    byCurriculum.set(key, curriculumPrefixes(curriculum, names, shortCode))
  })
  const all = Array.from(new Set(Array.from(byCurriculum.values()).flat())).sort(
    (a, b) => b.length - a.length
  )
  return {byCurriculum, all}
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Full stored name → display name for every class and level in `rows` whose display form
 * differs, longest full name first. Feeds `stripCurriculumPrefixesInText`. `label` (usually a
 * `buildCurriculumNameLabeler` result) gives the display form; without it the name only loses
 * its prefix.
 */
export const buildDisplayNamePairs = (
  index: CurriculumPrefixIndex,
  rows: ReadonlyArray<{name?: unknown; level?: unknown; curriculum?: unknown}>,
  label?: CurriculumNameLabeler
): Array<[string, string]> => {
  const pairs = new Map<string, [string, string]>()
  const add = (full: unknown, curriculum: unknown) => {
    const name = trim(full)
    if (!name || pairs.has(name.toLowerCase())) return
    const display = label ? label(name, curriculum) : displayCurriculumName(index, name, curriculum)
    if (display !== name) pairs.set(name.toLowerCase(), [name, display])
  }
  rows.forEach((row) => {
    add(row?.name, row?.curriculum)
    add(row?.level, row?.curriculum)
  })
  return Array.from(pairs.values()).sort((a, b) => b[0].length - a[0].length)
}

/**
 * Free text (AI chat turns, server warnings) with every known prefixed class/level name
 * replaced by its display form. Only whole names from `pairs` are touched — never a bare
 * curriculum word — so unrelated text is left exactly as written.
 */
export const stripCurriculumPrefixesInText = (
  text: string,
  pairs: ReadonlyArray<[string, string]>
): string => {
  if (!text || pairs.length === 0) return text
  const byLower = new Map(pairs.map(([full, display]) => [full.toLowerCase(), display]))
  const pattern = new RegExp(
    `(?<![\\p{L}\\p{N}])(${pairs.map(([full]) => escapeRegExp(full)).join('|')})(?![\\p{L}\\p{N}])`,
    'giu'
  )
  return text.replace(pattern, (match) => byLower.get(match.toLowerCase()) ?? match)
}

/**
 * Display text typed or picked by the user → the stored name it stands for: a full stored
 * name is kept, a display name that belongs to exactly one stored name maps back to it, and
 * anything else (a new or unmatched value) is returned as typed.
 */
export const resolveDisplayedName = (
  text: unknown,
  pairs: ReadonlyArray<[string, string]>,
  storedNames: readonly unknown[] = []
): string => {
  const typed = trim(text)
  if (!typed) return typed
  const typedLower = typed.toLowerCase()
  const exact = storedNames.map(trim).find((n) => n.toLowerCase() === typedLower)
  if (exact) return exact
  const matches = Array.from(
    new Set(pairs.filter(([, display]) => display.toLowerCase() === typedLower).map(([f]) => f))
  )
  return matches.length === 1 ? matches[0] : typed
}

/**
 * Display form of a level or class name. With the curriculum known only its prefixes apply;
 * without it every known prefix is tried (still only ever at the very start of the name).
 */
export const displayCurriculumName = (
  index: CurriculumPrefixIndex,
  name: unknown,
  curriculum?: unknown
): string => {
  const prefixes = trim(curriculum)
    ? (index.byCurriculum.get(lower(curriculum)) ?? curriculumPrefixes(curriculum))
    : index.all
  return stripCurriculumPrefix(name, prefixes)
}

// ---------------------------------------------------------------------------------------------
// One naming rule: the plain name, with the curriculum's short code in front only when another
// curriculum in the school has a level or class whose plain name reads the same. node-api
// applies the same rule to the `*_display_name` fields it returns.
// ---------------------------------------------------------------------------------------------

/**
 * The code put in front of a name another curriculum shares ("FBISE", "BISE KP"): the catalogue
 * short code, else the bracketed short form of the curriculum name, else the name itself.
 */
export const curriculumCodeFor = (curriculum: unknown, shortCode?: unknown): string =>
  trim(shortCode) || bracketedCurriculumCode(curriculum) || trim(curriculum)

/** A stored level/grade/class name with the curriculum it belongs to. */
export type CurriculumNameRow = {
  name?: unknown
  /** A class row's grade ("FBISE Class 1"), checked like a name. */
  grade?: unknown
  /** A class row's level ("FBISE Primary"), checked like a name. */
  level?: unknown
  curriculum?: unknown
  /** The curriculum's short code, when the row carries it (`curriculum_short_code`). */
  shortCode?: unknown
}

/** Stored name (+ its curriculum) → the name to show. Display only — never send it back. */
export type CurriculumNameLabeler = (storedName: unknown, curriculum?: unknown) => string

/** Lower-cased plain name → the lower-cased curricula that have a level or class reading so. */
const curriculaByPlainName = (
  index: CurriculumPrefixIndex,
  rows: ReadonlyArray<{name?: unknown; curriculum?: unknown}>
): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>()
  rows.forEach((row) => {
    const curriculum = lower(row?.curriculum)
    if (!curriculum) return
    const plain = lower(displayCurriculumName(index, row?.name, row?.curriculum))
    if (!plain) return
    const set = map.get(plain) ?? new Set<string>()
    set.add(curriculum)
    map.set(plain, set)
  })
  return map
}

/** True when a curriculum other than `curriculum` (lower-cased) has a name reading `plain`. */
const sharedWithAnother = (
  owners: Map<string, Set<string>>,
  plain: string,
  curriculum: string
): boolean => Array.from(owners.get(lower(plain)) ?? []).some((owner) => owner !== curriculum)

/** Every name a row carries (its own, its grade's and its level's) as a `{name, curriculum}` row. */
const expandNameRows = (rows: ReadonlyArray<CurriculumNameRow>) =>
  rows.flatMap((row) =>
    [row?.name, row?.grade, row?.level]
      .filter((name) => trim(name))
      .map((name) => ({name, curriculum: row?.curriculum, shortCode: row?.shortCode}))
  )

/**
 * The display labeller for level and class names (bugs 375/377). For a stored name it returns
 * the plain name ("Class 1", "Grade 7-A"); only when another curriculum in the school has a level
 * or class whose plain name reads the same does it put that curriculum's short code in front
 * ("FBISE Class 1" / "BISE KP Class 1"). A name with no curriculum at hand that is shared comes
 * back as stored (it still carries its prefix there). Every row's `name`, `grade` and `level`
 * count as names. `shortCodes` (curriculum name → short code, e.g.
 * `manualData.curriculumShortCodes`) wins over a row's own `shortCode`.
 */
export const buildCurriculumNameLabeler = (
  rows: ReadonlyArray<CurriculumNameRow>,
  shortCodes?: Record<string, string | null | undefined> | null
): CurriculumNameLabeler => {
  const names = expandNameRows(rows)
  const shortCodeByCurriculum = new Map<string, string>()
  Object.entries(shortCodes ?? {}).forEach(([curriculum, code]) => {
    if (trim(code)) shortCodeByCurriculum.set(lower(curriculum), trim(code))
  })
  names.forEach((row) => {
    const key = lower(row.curriculum)
    if (key && trim(row.shortCode) && !shortCodeByCurriculum.has(key))
      shortCodeByCurriculum.set(key, trim(row.shortCode))
  })
  const index = buildCurriculumPrefixIndex(
    names.map((row) => ({...row, shortCode: shortCodeByCurriculum.get(lower(row.curriculum))}))
  )
  const owners = curriculaByPlainName(index, names)

  return (storedName, curriculum) => {
    const plain = displayCurriculumName(index, storedName, curriculum)
    if (!plain) return plain
    const key = lower(curriculum)
    if (!key) return (owners.get(lower(plain))?.size ?? 0) > 1 ? trim(storedName) : plain
    if (!sharedWithAnother(owners, plain, key)) return plain
    const code = curriculumCodeFor(curriculum, shortCodeByCurriculum.get(key))
    return code ? `${code} ${plain}` : plain
  }
}

/** A level as the institute endpoints return it (`SELECT l.*` on `levels`). */
export type LevelCurriculumSource = {
  name?: unknown
  curriculum_name?: unknown
  curriculum_short_code?: unknown
}

/**
 * What the institute pages need to show a stored level, grade or class name: which curricula the
 * school has, the prefixes each one's names open with, and the short label shown beside a name.
 * Build it once per levels list with `buildSchoolCurricula`.
 */
export type SchoolCurricula = {
  index: CurriculumPrefixIndex
  /** Short label per lower-cased curriculum name ("FBISE", "BISE KP"). */
  labels: Map<string, string>
  /** Every known prefix, longest first, with the curriculum (lower-cased) it belongs to. */
  prefixOwners: Array<{prefix: string; curriculum: string}>
  /** The levels span two or more curricula (e.g. for a Curriculum column). */
  multiple: boolean
  /**
   * Lower-cased plain name → the lower-cased curricula with a level (or a class/grade passed to
   * `buildSchoolCurricula`) reading so. A name shared by two curricula gets its short code.
   */
  sharedNames: Map<string, Set<string>>
}

/**
 * Build the school's curricula from its levels. Levels without a curriculum are ignored.
 * `names` adds the stored class/grade names (with their level's `curriculum`) the page lists, so
 * "Grade 1-A" under two boards reads "FBISE Grade 1-A" / "BISE KP Grade 1-A"; without them only
 * level names are compared.
 */
export const buildSchoolCurricula = (
  levels: ReadonlyArray<LevelCurriculumSource> | null | undefined,
  names: ReadonlyArray<{name?: unknown; curriculum?: unknown}> = []
): SchoolCurricula => {
  const rows = (levels ?? [])
    .filter((level) => trim(level?.curriculum_name))
    .map((level) => ({
      name: level.name,
      curriculum: level.curriculum_name,
      shortCode: level.curriculum_short_code,
    }))
  const index = buildCurriculumPrefixIndex(rows)
  const labels = new Map<string, string>()
  rows.forEach((row) => {
    const key = lower(row.curriculum)
    if (labels.has(key)) return
    labels.set(key, curriculumCodeFor(row.curriculum, row.shortCode))
  })
  const prefixOwners = Array.from(index.byCurriculum.entries())
    .flatMap(([curriculum, prefixes]) => prefixes.map((prefix) => ({prefix, curriculum})))
    .sort((a, b) => b.prefix.length - a.prefix.length)
  const sharedNames = curriculaByPlainName(index, [
    ...rows,
    ...names.filter((row) => labels.has(lower(row?.curriculum))),
  ])
  return {index, labels, prefixOwners, multiple: labels.size > 1, sharedNames}
}

/**
 * The curriculum (lower-cased name) the first of `names` that opens with a known prefix belongs
 * to, or null. Pass a row's other names too ("Grade 1" has no prefix, its level "FBISE Primary"
 * does) so a name without a prefix still gets its curriculum label.
 */
export const detectCurriculum = (school: SchoolCurricula, ...names: unknown[]): string | null => {
  for (const name of names) {
    const value = lower(name)
    if (!value) continue
    const owner = school.prefixOwners.find(({prefix}) => value.startsWith(`${lower(prefix)} `))
    if (owner) return owner.curriculum
  }
  return null
}

/** A stored name split for display. */
export type CurriculumNameParts = {
  /**
   * The name to show: the plain name, or "<short code> <plain name>" when another curriculum in
   * the school has a name reading the same (the one naming rule, see `buildCurriculumNameLabeler`).
   */
  name: string
  /**
   * Badge text beside the name. Always null now: a shared name already carries its short code,
   * and a unique one needs none (bug 377 — no curriculum tag on every row). Kept for callers
   * that still render it.
   */
  curriculum: string | null
  /** The name without any curriculum prefix or code. */
  plain: string
  /** The short label of the name's curriculum when known ("FBISE"), for a Curriculum column. */
  curriculumLabel: string | null
}

/**
 * Split a stored level, grade or class name for display. The curriculum is `curriculum` when the
 * caller has it (the level's `curriculum_name`), else the one whose prefix the name opens with.
 * A trailing " - FBISE" the API adds to level labels ("FBISE Primary - FBISE (Main Branch)") is
 * dropped as well. The short code goes in front only when another curriculum has the same plain
 * name (`school.sharedNames`).
 */
export const splitCurriculumName = (
  school: SchoolCurricula,
  name: unknown,
  curriculum?: unknown
): CurriculumNameParts => {
  const full = trim(name)
  const unchanged = {name: full, curriculum: null, plain: full, curriculumLabel: null}
  if (!full || school.labels.size === 0) return unchanged
  const key = school.index.byCurriculum.has(lower(curriculum))
    ? lower(curriculum)
    : (detectCurriculum(school, full) ?? '')
  if (!key) return unchanged
  const prefixes = school.index.byCurriculum.get(key) ?? []
  let display = stripCurriculumPrefix(full, prefixes)
  for (const prefix of prefixes) {
    const suffix = new RegExp(`\\s+-\\s+${escapeRegExp(prefix)}(?=\\s*(\\(|$))`, 'i')
    if (suffix.test(display)) {
      display = display.replace(suffix, '')
      break
    }
  }
  const label = school.labels.get(key) ?? null
  const shared = Boolean(label) && sharedWithAnother(school.sharedNames ?? new Map(), display, key)
  return {
    name: shared ? `${label} ${display}` : display,
    curriculum: null,
    plain: display,
    curriculumLabel: label,
  }
}

/**
 * A stored level, grade or class name as plain display text: "Grade 1-B", or "FBISE Grade 1-B"
 * when another curriculum in the school has a "Grade 1-B" too. For option labels and other
 * text-only spots.
 */
export const formatCurriculumName = (
  school: SchoolCurricula,
  name: unknown,
  curriculum?: unknown
): string => splitCurriculumName(school, name, curriculum).name

/**
 * An option label built from a stored name and a stored detail in brackets (usually its level):
 * "Grade 1 (Primary (Main Branch))". Both follow the naming rule of `formatCurriculumName`; no
 * curriculum tag is appended.
 */
export const formatCurriculumOption = (
  school: SchoolCurricula,
  name: unknown,
  detail?: unknown,
  curriculum?: unknown
): string => {
  const key = school.index.byCurriculum.has(lower(curriculum))
    ? lower(curriculum)
    : detectCurriculum(school, name, detail)
  const main = splitCurriculumName(school, name, key)
  const sub = trim(detail) ? splitCurriculumName(school, detail, key) : null
  return sub?.name ? `${main.name} (${sub.name})` : main.name
}

/**
 * A stored level, grade or class name with its curriculum prefix (and the API's " - FBISE"
 * suffix) removed and no short code added, even when another curriculum shares the name.
 */
export const bareCurriculumName = (
  school: SchoolCurricula,
  name: unknown,
  curriculum?: unknown
): string => splitCurriculumName(school, name, curriculum).plain
